/*
  The header's menu row on large screens (snippets/header-dropdown-menu.liquid). Its dropdowns and mega panels are
  native disclosures, whose panels wait in a <template>; this file adds:
  - putting a panel in the page when something is about to open it: a pointer entering its item, the focus reaching the
    item, or the disclosure opening some other way. It says so with the event site-menu:stamped, and the panel's
    pictures fill from then on (assets/menu-pictures.js);
  - opening on hover (data-opens-on="hover", a mouse, from 769 px): a panel opens once the pointer rests on its
    item for 200 ms, and closes 250 ms after the pointer leaves the menu, so crossing a gap never closes it;
  - a head start for the pictures: when a mouse enters an item, the lazy pictures of its panel are told to load,
    so they are there by the time the panel opens (a lazy picture inside a closed panel waits otherwise); keyboard and
    touch open the panel first, and its pictures load as it shows;
  - one panel open at a time, each toggle's role and expanded state written as it connects and kept in step as it
    opens and closes;
  - Escape closes the open panel wherever the focus is, a hover-opened one included (WCAG 1.4.13); when the focus
    was in that item, it returns to the item's toggle;
  - a press anywhere else closes the open panel, which a hover-opened one needs, having no focus to lose;
  - a dropdown that would cross the window's right edge opens from its item's right edge instead;
  - the sidebar mega layout: the item the pointer or the focus reaches becomes the active one, and the panel's height
    follows its list. CSS already shows the list of the row that is hovered or has focus, so every list is reachable
    without this file; it keeps the last row active (tint, height) and gives touch and pens a way to reach every
    row: the first tap on a row with a list makes it active, the second follows its link;
  - the theme editor: selecting a mega menu block opens its panel (the first of the links it opens from), and
    leaving it closes the panel;
  - everything closes when the window crosses 769 px.
*/

if (!customElements.get('site-menu')) {
  const OPEN_DELAY = 200;
  const CLOSE_DELAY = 250;
  const FLIP = 'masthead__item--flip';
  const ACTIVE = 'is-active';

  class SiteMenu extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.largeScreen = windowFrom('large');
      this.items = [...this.querySelectorAll('.masthead__item')];
      // The markup has no role or expanded state on a toggle: without this script it is a plain disclosure. They are
      // written here as it connects and as a panel comes into the page, and onToggle keeps the state as each panel
      // opens and closes.
      this.markToggles(this);

      // toggle does not bubble, so it is caught on its way down.
      this.addEventListener('toggle', (event) => this.onToggle(event), { capture: true, signal });
      this.observers = new Map();
      this.pointer = '';
      document.addEventListener('keydown', (event) => this.onKeyDown(event), { signal });
      // What the last input was decides how a sidebar row is reached: a mouse hovers, a finger or a pen taps.
      this.addEventListener('pointerdown', (event) => (this.pointer = event.pointerType), { capture: true, signal });
      this.addEventListener('click', (event) => this.onClick(event), { signal });
      // pointerover bubbles, unlike pointerenter, so one listener serves every sidebar item.
      this.addEventListener('pointerover', (event) => this.activate(event.target, event), { signal });
      this.addEventListener(
        'focusin',
        (event) => {
          this.stamp(event.target.closest('.masthead__item'));
          this.activate(event.target);
        },
        { signal }
      );
      document.addEventListener(
        'pointerdown',
        (event) => {
          if (!this.contains(event.target)) this.closeAll();
        },
        { signal }
      );
      document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event, true), { signal });
      document.addEventListener('shopify:block:deselect', (event) => this.onBlockSelect(event, false), { signal });
      this.largeScreen.addEventListener('change', () => this.closeAll(), { signal });

      const hover = this.dataset.opensOn === 'hover';
      for (const item of this.items) {
        item.addEventListener('pointerenter', (event) => this.onEnter(event, item, hover), { signal });
      }
      if (!hover) return;
      this.addEventListener(
        'pointerleave',
        (event) => {
          if (this.isHover(event)) this.after(CLOSE_DELAY, () => this.closeAll());
        },
        { signal }
      );
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observers?.forEach((observer) => observer.disconnect());
      clearTimeout(this.timer);
    }

    isHover(event) {
      return event.pointerType === 'mouse' && this.largeScreen.matches;
    }

    dropdownOf(item) {
      return item.querySelector(':scope > header-menu > details');
    }

    markToggles(root) {
      for (const summary of root.querySelectorAll('details > summary')) {
        summary.setAttribute('role', 'button');
        summary.setAttribute('aria-expanded', String(summary.parentElement.open));
      }
    }

    /** Puts the item's panel in the page, out of its <template>, once. */
    stamp(item) {
      const template = item && this.dropdownOf(item)?.querySelector(':scope > template');
      if (!template) return;
      this.markToggles(template.content);
      template.replaceWith(template.content);
      this.dispatchEvent(new CustomEvent('site-menu:stamped', { bubbles: true }));
    }

    after(delay, action) {
      clearTimeout(this.timer);
      this.timer = setTimeout(action, delay);
    }

    onEnter(event, item, hover) {
      this.stamp(item);
      if (!this.isHover(event)) return;
      const details = this.dropdownOf(item);
      if (details) item.querySelectorAll('img[loading="lazy"]').forEach((image) => (image.loading = 'eager'));
      if (!hover) return;
      if (!details) {
        this.after(OPEN_DELAY, () => this.closeAll());
      } else if (details.open) {
        clearTimeout(this.timer);
      } else {
        this.after(OPEN_DELAY, () => {
          details.open = true;
        });
      }
    }

    closeAll(except = null) {
      for (const item of this.items) {
        const details = this.dropdownOf(item);
        if (details && details !== except && details.open) details.open = false;
      }
    }

    onToggle(event) {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement)) return;
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', String(details.open));

      const item = details.parentElement?.closest('.masthead__item');
      if (!item || this.dropdownOf(item) !== details) return;
      item.classList.remove(FLIP);
      if (!details.open) {
        this.unfollow(details);
        return;
      }
      this.stamp(item);
      this.closeAll(details);
      const active = details.querySelector(`.mega-sidebar__item.${ACTIVE}`);
      if (active) this.follow(active);
      const panel = details.querySelector(':scope > .header__submenu');
      if (panel && panel.getBoundingClientRect().right > document.documentElement.clientWidth - 5) {
        item.classList.add(FLIP);
      }
    }

    onKeyDown(event) {
      this.pointer = 'keyboard';
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      const open = this.items.map((item) => this.dropdownOf(item)).filter((details) => details?.open);
      if (open.length === 0) return;
      // The toggle gets the focus back only when the focus was in the item that closes.
      const focused = open.find((details) => details.closest('.masthead__item').contains(document.activeElement));
      open.forEach((details) => (details.open = false));
      focused?.querySelector(':scope > summary')?.focus();
    }

    // A finger or a pen cannot hover: its first tap on a row that has a list shows the list, the second follows the
    // link.
    onClick(event) {
      if (this.pointer !== 'touch' && this.pointer !== 'pen') return;
      const item = event.target.closest?.('.mega-sidebar__link')?.closest('.mega-sidebar__item--parent');
      if (!item || item.classList.contains(ACTIVE)) return;
      event.preventDefault();
      this.select(item);
    }

    // A sidebar item that has a list becomes the active one when the mouse or the keyboard focus reaches it (a tap
    // that focuses a link is handled by onClick).
    activate(target, event) {
      if (event && event.pointerType !== 'mouse') return;
      if (!event && (this.pointer === 'touch' || this.pointer === 'pen')) return;
      const item = target.closest?.('.mega-sidebar__item--parent');
      if (item) this.select(item);
    }

    select(item) {
      if (item.classList.contains(ACTIVE)) return;
      item.parentElement.querySelector(`.${ACTIVE}`)?.classList.remove(ACTIVE);
      item.classList.add(ACTIVE);
      this.follow(item);
    }

    // The sidebar's panel is as tall as the active list (at least its minimum): the list's height is published on
    // the sidebar, which takes it as its minimum height, and kept up to date while the list changes size. Each panel
    // owns its observer, so one panel closing never stops the one that opens next.
    follow(item) {
      const sidebar = item.closest('.mega-sidebar');
      const list = item.querySelector(':scope > .mega-sidebar__panel');
      if (!sidebar || !list) return;
      this.observers.get(sidebar)?.disconnect();
      const observer = new ResizeObserver(() => {
        // A list that CSS has given way to (hover on another row) measures nothing: keep the last height.
        if (list.offsetHeight > 0) sidebar.style.setProperty('--mega-panel-height', `${list.offsetHeight}px`);
      });
      observer.observe(list);
      this.observers.set(sidebar, observer);
    }

    unfollow(details) {
      const sidebar = details.querySelector('.mega-sidebar');
      this.observers.get(sidebar)?.disconnect();
      this.observers.delete(sidebar);
    }

    // A mega menu block's attributes are on the first item it opens from (snippets/header-dropdown-menu.liquid), the
    // first marked with its id. Below 769 px the menu row is hidden and there is no panel to open.
    onBlockSelect(event, selected) {
      const id = event.detail?.blockId;
      const item = id && this.largeScreen.matches ? this.querySelector(`[data-mega-block="${CSS.escape(id)}"]`) : null;
      const details = item ? this.dropdownOf(item) : null;
      if (!details) return;
      if (selected) this.closeAll(details);
      details.open = selected;
    }
  }

  customElements.define('site-menu', SiteMenu);
}
